import React, { useState, Component } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Copy, ArrowRight, ShoppingBag } from 'lucide-react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { formatCurrency } from '../lib/utils';
import { Restaurant, CartItem } from '../lib/types';
import { useLoyaltySummary } from '../lib/loyalty-hooks';
import { Sparkles, Gift } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { supabase } from '../lib/supabase';
interface OrderState {
  restaurant: Restaurant;
  orderNumber: string;
  trackingToken: string;
  orderId?: string;
  customerName: string;
  customerPhone: string;
  deliveryType: string;
  paymentMethod?: string;
  deliveryAddress?: string | null;
  mapsUrl?: string | null;
  notes?: string | null;
  ticketImageUrl?: string | null;
  items: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  customerTrackingVisible?: boolean;
  preorderDate?: string | null;
  preorderTimeSlot?: string | null;
  paymentProofUrl?: string | null;
  deliveryConfirmationPending?: boolean;
  // MS Express external delivery context for the WhatsApp message
  usesExternalDelivery?: boolean;
  isEmilius?: boolean;
  deliveryZone?: string | null;
  deliverySubzone?: string | null;
  foodSubtotal?: number | null;
}
export function OrderConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as OrderState | null;
  const [copied, setCopied] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const {
    t
  } = useLanguage();
  if (!state) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-7563" data-mp="j0dwmh38gfnj">
        <MarketplaceHeader data-id="element-7564" data-mp="strxh52ttid3" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-7565" data-mp="ssyvehetbh1a">
          <EmptyState icon={ShoppingBag} title={t('confirm_no_order')} description="" actionLabel={t('confirm_back')} onAction={() => navigate('/')} data-id="element-7566" data-mp="otzjgcunyk0c" />
        </main>
        <Footer data-id="element-7567" data-mp="exo2vao1j803" />
      </div>;
  }
  const isEmiliusOrder = Number((state.restaurant as Restaurant)?.id) === 27 || (state.restaurant as Restaurant)?.slug === 'emilius-pizza';
  const {
    restaurant,
    orderNumber,
    trackingToken,
    items,
    subtotal,
    deliveryFee,
    total
  } = state;
  const showTracking = state.customerTrackingVisible !== false;
  const isEmilius = Number(restaurant?.id) === 27 || restaurant?.slug === 'emilius-pizza';
  const {
    summary
  } = useLoyaltySummary(state.customerPhone, restaurant.id);
  // Restaurants with full KDS integration don't need a WhatsApp handoff —
  // the order is already received automatically by the kitchen.
  //
  // NatuCafé (id 19) SÍ debe mostrar el handoff de WhatsApp en el flujo de
  // marketplace (delivery / llevar / comer en el local) igual que los demás
  // restaurantes — el cliente envía el pedido por WhatsApp. El flujo QR de
  // mesa (autoatención) NO pasa por esta pantalla, así que no se ve afectado.
  const isDirectKdsRestaurant = String(restaurant.id) === '28' || restaurant.slug === 'rancho-san-francisco';
  const handleCopy = async () => {
    if (!restaurant.whatsapp_number) return;
    const restaurantMapsUrl = restaurant.lat != null && restaurant.lng != null ? `https://maps.google.com/?q=${restaurant.lat},${restaurant.lng}` : restaurant.address_text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address_text)}` : null;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken,
      customerTrackingVisible: showTracking,
      deliveryDate: state.preorderDate,
      deliveryTimeSlot: state.preorderTimeSlot,
      paymentProofUrl: state.paymentProofUrl,
      deliveryConfirmationPending: state.deliveryConfirmationPending,
      restaurantAddress: restaurant.address_text,
      restaurantMapsUrl,
      usesExternalDelivery: state.usesExternalDelivery,
      isEmilius: state.isEmilius,
      deliveryZone: state.deliveryZone,
      deliverySubzone: state.deliverySubzone,
      foodSubtotal: state.foodSubtotal
    });
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };
  const handleWhatsApp = () => {
    if (!restaurant.whatsapp_number) {
      console.error('[NOIZA] No whatsapp_number — cannot open WhatsApp');
      return;
    }
    const popup = window.open('', '_blank');
    const restaurantMapsUrl = restaurant.lat != null && restaurant.lng != null ? `https://maps.google.com/?q=${restaurant.lat},${restaurant.lng}` : restaurant.address_text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address_text)}` : null;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken,
      customerTrackingVisible: showTracking,
      deliveryDate: state.preorderDate,
      deliveryTimeSlot: state.preorderTimeSlot,
      paymentProofUrl: state.paymentProofUrl,
      deliveryConfirmationPending: state.deliveryConfirmationPending,
      restaurantAddress: restaurant.address_text,
      restaurantMapsUrl,
      usesExternalDelivery: state.usesExternalDelivery,
      isEmilius: state.isEmilius,
      deliveryZone: state.deliveryZone,
      deliverySubzone: state.deliverySubzone,
      foodSubtotal: state.foodSubtotal
    });
    const whatsappUrl = buildWhatsAppUrl(restaurant.whatsapp_number, message);
    if (popup) {
      popup.location.href = whatsappUrl;
    } else {
      window.location.href = whatsappUrl;
    }
    setWhatsappSent(true);
  };
  const trackingUrl = `https://track.${restaurant.domain || 'restaurant.com'}/${trackingToken}`;
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy tracking link', err);
    }
  };
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-7568" data-mp="27dsto13rhqa6">
      <MarketplaceHeader forceEmilius={isEmilius} data-id="element-7569" data-mp="27fviwix3bfdu" />

      <main className="flex-1 flex items-center justify-center p-4 py-12" data-id="element-7570" data-mp="2ce6r6p7qlhty">
        <div className="max-w-2xl mx-auto w-full" data-id="element-7571" data-mp="2hbp2gm988954">
          {/* Header */}
          <div className="text-center mb-8" data-id="element-7572" data-mp="1s92y8f317jfg">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDirectKdsRestaurant || whatsappSent ? 'bg-green-100' : 'bg-amber-100'}`} data-id="element-7573" data-mp="1s805s2q298xf">
              {isDirectKdsRestaurant || whatsappSent ? <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-7574" data-mp="1x8fjwwi62kgg" /> : <MessageCircle className="w-10 h-10 text-amber-600" data-id="element-7575" data-mp="19il88i9l31xs" />}
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3" data-id="element-7576" data-mp="14jh260hyhkbo">
              {isDirectKdsRestaurant ? 'Pedido enviado exitosamente' : whatsappSent ? '¡Pedido enviado!' : '¡Ya casi está listo!'}
            </h1>
            <p className="text-xl text-gray-600" data-id="element-7577" data-mp="1di2idozygn9a">
              {isDirectKdsRestaurant ? `Tu pedido ya está en cocina · Orden #${orderNumber}` : whatsappSent ? 'Tu pedido ha sido recibido por el restaurante' : `Orden #${orderNumber}`}
            </p>
          </div>

          {state.deliveryConfirmationPending && <div role="status" className="mb-8 rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 text-center" data-id="element-7578" data-mp="gh3ns56gpmqk">
              <p className="text-base sm:text-lg font-black leading-relaxed text-amber-950" data-id="element-7579" data-mp="6p6bsw7mqqqs">
                LA PIZZERÍA TIENE QUE CONFIRMAR EL DELIVERY A TU UBICACIÓN, POR
                FAVOR CONTINUA EL PROCESO Y ENVIAR EL MENSAJE DE WHATSAPP, LUEGO
                ESPERA A SER CONTACTADO POR LA PIZZERÍA PARA FINALIZAR TU
                PEDIDO.
              </p>
            </div>}

          {/* WhatsApp CTA — only before sending, skipped for direct KDS restaurants */}
          {!isDirectKdsRestaurant && !whatsappSent && restaurant.whatsapp_number && <div className="mb-8" data-id="element-7580" data-mp="3a6y6wjh2bqt7">
                <button onClick={handleWhatsApp} className="w-full h-16 rounded-2xl text-xl font-black bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-emerald-200 flex items-center justify-center gap-3 transition-all animate-pulse" data-id="element-7581" data-mp="3a7zlgxxaevfo">
                  <MessageCircle className="w-7 h-7" data-id="element-7582" data-mp="1gsdyw2ukhdb9" />
                  Enviar pedido por WhatsApp
                </button>

                <div className="mt-4 p-4 rounded-xl bg-red-50 border-2 border-red-300" data-id="element-7583" data-mp="17rz8ip2qx80w">
                  <p className="text-red-600 font-black text-base sm:text-lg text-center leading-tight" data-id="element-7584" data-mp="17uc1rydyf4t9">
                    ⚠️ SI NO ENVÍAS POR WHATSAPP, TU PEDIDO NO SE REALIZA
                  </p>
                </div>
              </div>}

          {/* Success confirmation after WhatsApp sent */}
          {whatsappSent && <div className="mb-8 p-5 rounded-2xl bg-green-50 border-2 border-green-200 text-center" data-id="element-7585" data-mp="2s7kry48msa7b">
              <p className="text-green-700 font-bold text-lg" data-id="element-7586" data-mp="2x75ns5si82ft">
                ✅ Tu pedido ha sido enviado al restaurante. ¡Gracias!
              </p>
            </div>}

          {/* Order Number Card */}
          <Card className="p-8 mb-8 text-center border-2 border-brand-primary/20 bg-brand-primary/5" data-id="element-7587" data-mp="gls9kiaeo51v">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7588" data-mp="vay8s698tv9r">
              {t('confirm_order_number')}
            </p>
            <p className="text-5xl font-black text-brand-primary tracking-tight" data-id="element-7589" data-mp="149s1hljge1i1">
              #{orderNumber}
            </p>
          </Card>

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-7590" data-mp="10aj8wk05g9o2">
            {showTracking && <Button onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 text-lg bg-gray-900 hover:bg-gray-800 text-white" data-id="element-7591" data-mp="1e652l4hbvhmt">
                <ShoppingBag className="w-5 h-5 mr-2" data-id="element-7592" data-mp="3fzul5ui0tdq4" />
                {t('confirm_track')}
              </Button>}

            {whatsappSent && restaurant.whatsapp_number && <Button onClick={handleWhatsApp} variant="outline" className="h-14 text-lg border-2 hover:bg-green-50 hover:text-green-600 hover:border-green-200" data-id="element-7593" data-mp="1ez7lyrsmqo9o">
                <MessageCircle className="w-5 h-5 mr-2 text-green-600" data-id="element-7594" data-mp="1o0e39333s33z" />
                Reenviar por WhatsApp
              </Button>}
          </div>

          {/* Tracking Link */}
          {showTracking && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-8" data-id="element-7595" data-mp="22usf8jo92c95">
              <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-7596" data-mp="27maen3amk4s0">
                {t('copy_tracking')}
              </p>
              <div className="flex items-center gap-3" data-id="element-7597" data-mp="2bmkoir1jffc5">
                <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-7598" data-mp="2gmqk2iaxhlzi">
                  {trackingUrl}
                </div>
                <Button variant="outline" onClick={handleCopyLink} className="shrink-0" data-id="element-7599" data-mp="2ljpdeymrpown">
                  {copied ? <span className="text-green-600 flex items-center" data-id="element-7600" data-mp="2qpcelw9858yp">
                      <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-7601" data-mp="2lsc4995cdk1s" />
                      {t('confirm_copied')}
                    </span> : <>
                      <Copy className="w-4 h-4 mr-2" data-id="element-7602" data-mp="3eghsi5vltr6d" />
                      {t('confirm_copy')}
                    </>}
                </Button>
              </div>
            </div>}

          {/* Loyalty Summary Block */}
          {summary && <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] rounded-2xl p-6 text-white shadow-lg border border-[#333] mb-8" data-id="element-7603" data-mp="30p36dq6eh9v3">
              <div className="flex items-center gap-3 mb-4" data-id="element-7604" data-mp="124zeh8kkg9m">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0" data-id="element-7605" data-mp="3sg9a0glbxpp4">
                  <Sparkles className="w-5 h-5 text-[#F5E9DA]" data-id="element-7606" data-mp="3cxgce03nb4ek" />
                </div>
                <div data-id="element-7607" data-mp="3hwt34vxn0vmf">
                  <h3 className="font-bold text-lg" data-id="element-7608" data-mp="3hxchk2o2x1vp">¡Ganaste puntos!</h3>
                  <p className="text-sm text-white/70" data-id="element-7609" data-mp="33xkxplavsr9s">
                    Tu nivel actual: {summary.current_level || 'Inicial'}
                  </p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-7610" data-mp="3voha6kt14twj">
                <p className="text-sm font-medium" data-id="element-7611" data-mp="3vqtrcvqyr08j">
                  Tienes un total de{' '}
                  <span className="font-bold text-[#F5E9DA]" data-id="element-7612" data-mp="4kagxhp1hcar">
                    {summary.current_points} puntos
                  </span>{' '}
                  acumulados.
                </p>
              </div>

              {summary.available_rewards_count > 0 && <div className="mt-4 flex items-start gap-3 bg-[#006847]/20 border border-[#006847]/30 rounded-xl p-4" data-id="element-7613" data-mp="ih5wzu3pk40b">
                  <Gift className="w-5 h-5 text-[#006847] shrink-0 mt-0.5" data-id="element-7614" data-mp="nas99l5ym5vl" />
                  <p className="text-sm font-medium text-white" data-id="element-7615" data-mp="27xrvgcy9owrm">
                    ¡Tienes {summary.available_rewards_count}{' '}
                    {summary.available_rewards_count === 1 ? 'beneficio disponible' : 'beneficios disponibles'}{' '}
                    para tu próxima compra!
                  </p>
                </div>}
            </div>}

          {/* Order Details */}
          <Card className="p-6 mb-8" data-id="element-7616" data-mp="1k1dunhvv6f8y">
            <h3 className="font-bold text-gray-900 mb-4" data-id="element-7617" data-mp="1k2ffuf6lgi4e">
              {t('order_summary')}
            </h3>
            <div className="space-y-4" data-id="element-7618" data-mp="1yzzhbc4a7v5t">
              <div className="flex justify-between" data-id="element-7619" data-mp="1tzbjo7ljsg2m">
                <span className="text-gray-600" data-id="element-7620" data-mp="1txqmsbbrgl1l">
                  {t('confirm_order_number')}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-7621" data-mp="3afo68913j499">
                  {orderNumber}
                </span>
              </div>
              {state.deliveryConfirmationPending ? <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-id="element-7622" data-mp="3chf3l5q2vdz8">
                  <p className="font-bold text-amber-900" data-id="element-7623" data-mp="3hf6sdqvfejz7">
                    Delivery y costo pendientes de confirmación
                  </p>
                  <p className="mt-1 text-sm text-amber-800" data-id="element-7624" data-mp="3rexry6yufp65">
                    Enviá el pedido por WhatsApp y esperá el contacto de la
                    pizzería antes de realizar cualquier pago.
                  </p>
                </div> : <>
                  <div className="flex justify-between" data-id="element-7625" data-mp="2ynxhbd60bn5i">
                    <span className="text-gray-600" data-id="element-7626" data-mp="33ol8byu8k3zq">{t('total')}</span>
                    <span className="font-semibold text-gray-900" data-id="element-7627" data-mp="38glfoccmxz6c">
                      {formatCurrency(total)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-7628" data-mp="2ayl9ohqbvn5b">
                    <span className="text-gray-600" data-id="element-7629" data-mp="2fyqzdsd1ezkr">
                      {t('checkout_payment')}
                    </span>
                    <span className="text-gray-600" data-id="element-7630" data-mp="x3v2qdojzdf5">
                      {state.paymentMethod || 'No especificado'}
                    </span>
                  </div>
                </>}
              {state.deliveryAddress && <div className="flex justify-between" data-id="element-7631" data-mp="j5em9v0w28jt">
                  <span className="text-gray-600" data-id="element-7632" data-mp="j4myh6dj9f43">{t('checkout_address')}</span>
                  <span className="text-gray-600" data-id="element-7633" data-mp="e6lgkoruflu7">{state.deliveryAddress}</span>
                </div>}
            </div>
          </Card>

          {/* Bottom actions */}
          <div className="space-y-4" data-id="element-7634" data-mp="5ejlz74ggzru">
            {!isDirectKdsRestaurant && restaurant.whatsapp_number && <>
                <Button size="lg" fullWidth onClick={handleWhatsApp} className="bg-[#25D366] hover:bg-[#20bd5a] text-white border-none shadow-lg shadow-emerald-200 h-14 text-lg" data-id="element-7635" data-mp="g89spqoud6g">
                  <MessageCircle className="w-6 h-6 mr-2" data-id="element-7636" data-mp="1fbgex57ocz19" />
                  {whatsappSent ? 'Reenviar por WhatsApp' : t('confirm_whatsapp')}
                </Button>

                <Button variant="secondary" size="lg" fullWidth onClick={handleCopy} className="h-14" data-id="element-7637" data-mp="15bfrb72rvwr9">
                  <Copy className="w-5 h-5 mr-2" data-id="element-7638" data-mp="vkuwluq7zcdm" />
                  {copied ? t('confirm_copied') : t('confirm_copy')}
                </Button>
              </>}

            {showTracking && <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 border border-gray-200" data-id="element-7639" data-mp="mnkhfyaa733f">
                {t('confirm_track')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-7640" data-mp="hlbq16d2493f" />
              </Button>}
          </div>
        </div>
      </main>

      <Footer forceEmilius={isEmiliusOrder} data-id="element-7641" data-mp="3u8288xigvkvx" />
    </div>;
}